/**
 * `store.action` events for the telemetry `diagnostics` group: a
 * zustand middleware placed between `devtools` and `redux` in store/runStore.ts.
 *
 * It records an action's `type` and id fields only (`runId`, `conversationId`,
 * `traceId`, `forecastId`, `parentRunId`, `rootIds`, and `meta.intent_id` when a command
 * dispatched it under an intent), never its content, and only
 * after the reducer has returned, from a queued microtask, so no telemetry work runs
 * inside a reducer or a React update. An action the reducer answered with the same
 * state (a no-op) is not recorded. High-rate types are coalesced per type and run into at most one event
 * per second with their count and first / last `t_mono`.
 */
import { recordTraceEvent, telemetryGroupOn } from "./groups.js";
import { isIntentId } from "./intentEmitter.js";

/** Action types dispatched at a high rate, coalesced per run and second. */
export const PROGRESS_ACTIONS = new Set([
  "RUN_PROGRESS",
  "RUN_UPDATE_APPLIED",
  "RUN_NARRATION_APPENDED",
  "RUN_POINTERS_MERGED",
]);
export const PROGRESS_WINDOW_MS = 1000;
const ID_KEYS = ["runId", "conversationId", "traceId", "forecastId", "parentRunId"];

const monoNow = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

/** The recorded fields of `action`: its type and string id fields. */
export function actionFields(action) {
  const out = { type: String(action.type) };
  // RUN_CREATED carries the new run whole (`{type, run}`): its ids, never its content.
  const run = action.run && typeof action.run === "object" ? action.run : {};
  for (const k of ID_KEYS) {
    const v = typeof action[k] === "string" ? action[k] : run[k];
    if (typeof v === "string") out[k] = v;
  }
  if (Array.isArray(action.rootIds)) {
    out.rootIds = action.rootIds.filter((x) => typeof x === "string").slice(0, 16);
  }
  // The intent of the command that dispatched it (lib/telemetry/intentDeps.ts).
  const intentId = action.meta?.intent_id;
  if (isIntentId(intentId)) out.intent_id = intentId;
  return out;
}

/** The recorder behind the middleware; injectable for tests. */
export function createStoreTracer({
  record = recordTraceEvent,
  enabled = () => telemetryGroupOn("diagnostics"),
  now = monoNow,
  setTimeoutImpl = (fn, ms) => setTimeout(fn, ms),
} = {}) {
  /** Pending progress runs by `type|runId`. */
  const runs = new Map();

  function flush(key) {
    const r = runs.get(key);
    runs.delete(key);
    if (!r) return;
    try {
      record({
        event: "store.action",
        ...r.fields,
        count: r.count,
        first_t_mono: r.first,
        last_t_mono: r.last,
      });
    } catch {
      /* never surface */
    }
  }

  function observe(action) {
    if (!action || typeof action.type !== "string" || !enabled()) return;
    try {
      const fields = actionFields(action);
      if (!PROGRESS_ACTIONS.has(fields.type)) {
        record({ event: "store.action", ...fields });
        return;
      }
      const t = now();
      const key = `${fields.type}|${fields.runId || ""}`;
      const r = runs.get(key);
      if (r) {
        r.count += 1;
        r.last = t;
        return;
      }
      runs.set(key, { fields, count: 1, first: t, last: t });
      setTimeoutImpl(() => flush(key), PROGRESS_WINDOW_MS);
    } catch {
      /* never surface */
    }
  }
  /** Whether a dispatch needs queueing at all (the group is on). */
  observe.active = enabled;
  return observe;
}

const defaultTracer = createStoreTracer();

/** Run `fn` after the current task's synchronous work, as a microtask. */
const defer =
  typeof queueMicrotask === "function"
    ? (fn) => queueMicrotask(fn)
    : (fn) => {
        Promise.resolve().then(fn);
      };

/**
 * zustand middleware: wraps the `dispatch` the inner `redux` middleware installs so
 * each action is observed after its reducer returned.
 */
export function traceActions(config, observe = defaultTracer) {
  return (set, get, api) => {
    const state = config(set, get, api);
    const dispatch = api.dispatch;
    if (typeof dispatch === "function") {
      api.dispatch = (action) => {
        const before = get();
        const result = dispatch(action);
        // The reducer returns the same state for a no-op; such an action changed nothing.
        if (get() === before) return result;
        if (!observe.active || observe.active()) defer(() => observe(action));
        return result;
      };
    }
    return state;
  };
}
